import type { HousingDetailedHistoryScope, HousingHistoryWindowResult } from "./housingHistoryContract.ts";
import { HOUSING_CANONICAL_1D_HISTORY_SCOPES, HOUSING_HTS_SCOPE_BY_ID, HOUSING_TAPE_CONTRACT, type HousingViewportPreset } from "./housingHtsRegistry.ts";
import { buildHousingTechnicalPoints, type HousingMacdPreset, type HousingMarketBar, type HousingMarketChartScopeData } from "./housingTechnicalChart.ts";

export const HOUSING_CANONICAL_1D_BROWSER_UI_ENABLED = true as const;
export const HOUSING_CANONICAL_1D_PAGE_SIZE = 18 as const;

export type HousingCanonicalDisplayResolution = "1d" | "3d" | "7d" | "14d" | "1m";
export const HOUSING_CANONICAL_DISPLAY_RESOLUTIONS: readonly HousingCanonicalDisplayResolution[] = Object.freeze(["1d","3d","7d","14d","1m"]);

export interface HousingCanonicalDailyRow {
  startDate: string;
  endDate: string;
  periodEndDate?: string;
  lastObservedDate: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  transactionCount: number;
  medianPriceProxyKrw: number | null;
  provisional?: boolean;
  primaryLogPriceChangePct: number | null;
  matchedCellCount: number;
  matchedPreviousTransactionCount: number;
  matchedCurrentTransactionCount: number;
  originCellCount: number;
  originPreviousTransactionCoveragePct: number;
  originCellSurvivalPct: number;
  segmentId: string | null;
  status: "anchor" | "ready" | "insufficient";
  anchor: boolean;
  strictFixedLogPriceChangePct?: number | null;
  strictFixedIndexOpen?: number | null;
  strictFixedIndexHigh?: number | null;
  strictFixedIndexLow?: number | null;
  strictFixedIndexClose?: number | null;
  strictFixedStatus?: "anchor" | "ready" | "insufficient";
  strictFixedSegmentId?: string | null;
}
export interface HousingCanonicalDisplayRow extends HousingCanonicalDailyRow {
  resolution: HousingCanonicalDisplayResolution;
  dailyBarCount: number;
  marketValidDayCount?: number;
}
export interface HousingCanonicalHistoryPage {
  status: "disabled" | "ready";
  rows: HousingCanonicalDailyRow[];
  nextOffset: number;
  done: boolean;
  totalMatchingChunks: number;
  prefetchedKeys: string[];
}
export interface HousingCanonicalHistoryClient {
  loadWindow(input: {
    sourceGenerationId: string;
    scope: HousingDetailedHistoryScope;
    resolution: "1d";
    offset: number;
    limit: number;
    direction: "backward";
  }, signal?: AbortSignal): Promise<HousingHistoryWindowResult>;
}
export type HousingCanonicalViewport = { start: number; end: number };

export function shouldLoadCanonicalOlder(candidateStart:number,currentSpan:number,done=false,loading=false){
  if(done||loading)return false;
  const threshold=Math.max(2,Math.ceil(Math.max(1,currentSpan)*0.4));
  return candidateStart<=threshold;
}

type Row = Record<string, any>;
const DATE=/^\d{4}-\d{2}-\d{2}$/;
const DAY_MS=86_400_000;
const ANCHOR_DAY=Math.floor(Date.UTC(1970,0,5)/DAY_MS);
const MIN_VISIBLE_BARS=6;

function finite(value: unknown): value is number { return typeof value === "number" && Number.isFinite(value); }
function record(value: unknown,label: string): Row { if(!value||typeof value!=="object"||Array.isArray(value))throw new Error(label); return value as Row; }
export function canonicalHistoryAvailableScopes(reference: unknown): HousingDetailedHistoryScope[] {
  const document=record(reference,"invalid_housing_canonical_history_reference"),scopes=record(document.scopes,"invalid_housing_canonical_history_reference_scopes");
  const available=HOUSING_CANONICAL_1D_HISTORY_SCOPES.filter(scope=>Object.prototype.hasOwnProperty.call(scopes,scope));
  if(!available.length)throw new Error("empty_housing_canonical_history_scopes");
  return [...available];
}
function nullableFinite(value: unknown,label: string): number|null {
  if(value==null)return null;
  const n=Number(value); if(!finite(n))throw new Error(label); return n;
}
function nonnegative(value:unknown,label:string){const n=Number(value??0);if(!finite(n)||n<0)throw new Error(label);return n;}
function nonnegativeInteger(value:unknown,label:string){const n=Number(value??0);if(!Number.isInteger(n)||n<0)throw new Error(label);return n;}
function dateSerial(value: string){
  if(!DATE.test(value))throw new Error("invalid_housing_canonical_history_date");
  const ms=Date.parse(`${value}T00:00:00Z`); if(!Number.isFinite(ms))throw new Error("invalid_housing_canonical_history_date");
  return Math.floor(ms/DAY_MS);
}
function serialDate(serial: number){ return new Date(serial*DAY_MS).toISOString().slice(0,10); }
function resolutionDays(resolution: Exclude<HousingCanonicalDisplayResolution,"1m">){ return resolution==="1d"?1:Number(resolution.slice(0,-1)); }
function bucketBounds(day: string,resolution: HousingCanonicalDisplayResolution){
  if(resolution==="1d")return {start:day,end:day};
  if(resolution==="1m"){
    const [year,month]=day.slice(0,7).split("-").map(Number);
    const start=`${String(year).padStart(4,"0")}-${String(month).padStart(2,"0")}-01`;
    const end=new Date(Date.UTC(year,month,0)).toISOString().slice(0,10);
    return {start,end};
  }
  const size=resolutionDays(resolution),serial=dateSerial(day),start=ANCHOR_DAY+Math.floor((serial-ANCHOR_DAY)/size)*size;
  return {start:serialDate(start),end:serialDate(start+size-1)};
}
function validIndexRow(row: HousingCanonicalDailyRow){
  return (row.status==="anchor"||row.status==="ready") && finite(row.open) && finite(row.high) && finite(row.low) && finite(row.close) && Boolean(row.segmentId);
}
function validStrictIndexRow(row: HousingCanonicalDailyRow){
  return (row.strictFixedStatus==="anchor"||row.strictFixedStatus==="ready") && finite(row.strictFixedIndexOpen) && finite(row.strictFixedIndexHigh) && finite(row.strictFixedIndexLow) && finite(row.strictFixedIndexClose) && Boolean(row.strictFixedSegmentId);
}
function parseDailyRows(result: HousingHistoryWindowResult,bandId="all"){
  if(result.resolution!=="1d")throw new Error("noncanonical_housing_history_resolution");
  const out:HousingCanonicalDailyRow[]=[];
  for(const rawChunk of result.chunks){
    const chunk=record(rawChunk,"invalid_housing_canonical_history_chunk");
    if(chunk.resolution!=="1d")throw new Error("noncanonical_housing_history_chunk");
    const bands=record(chunk.barsByBand,"invalid_housing_canonical_history_bands"),bars=bands[bandId];
    if(!/^[a-z0-9_]+$/.test(bandId)||!Array.isArray(bars))throw new Error("invalid_housing_canonical_history_band");
    for(const rawBar of bars){
      const bar=record(rawBar,"invalid_housing_canonical_history_bar");
      const startDate=String(bar.startDate??""),endDate=String(bar.endDate??"");
      if(!DATE.test(startDate)||!DATE.test(endDate))throw new Error("invalid_housing_canonical_history_date");
      const status=String(bar.primaryIndexStatus??"insufficient"),strictStatus=String(bar.strictFixedStatus??"insufficient");
      if(!["anchor","ready","insufficient"].includes(status)||!["anchor","ready","insufficient"].includes(strictStatus))throw new Error("invalid_housing_canonical_history_status");
      if(bar.sourceResolution!=null&&bar.sourceResolution!=="1d")throw new Error("noncanonical_housing_history_source");
      const transactionCount=Number(bar.transactionCount??0);
      if(!Number.isInteger(transactionCount)||transactionCount<0)throw new Error("invalid_housing_canonical_history_transaction_count");
      const lastObservedDate=String(bar.lastObservedDate??endDate),periodEndDate=String(bar.periodEndDate??endDate);
      if(!DATE.test(lastObservedDate)||!DATE.test(periodEndDate))throw new Error("invalid_housing_canonical_history_date");
      const row:HousingCanonicalDailyRow={
        startDate,endDate,periodEndDate,lastObservedDate,
        open:nullableFinite(bar.primaryIndexOpen,"invalid_housing_canonical_history_open"),
        high:nullableFinite(bar.primaryIndexHigh,"invalid_housing_canonical_history_high"),
        low:nullableFinite(bar.primaryIndexLow,"invalid_housing_canonical_history_low"),
        close:nullableFinite(bar.primaryIndexClose,"invalid_housing_canonical_history_close"),
        transactionCount,
        medianPriceProxyKrw:nullableFinite(bar.medianPriceProxyKrw,"invalid_housing_canonical_history_median_price"),
        primaryLogPriceChangePct:nullableFinite(bar.primaryLogPriceChangePct,"invalid_housing_canonical_history_log_return"),
        matchedCellCount:nonnegativeInteger(bar.matchedCellCount,"invalid_housing_canonical_history_matched_cells"),
        matchedPreviousTransactionCount:nonnegativeInteger(bar.matchedPreviousTransactionCount,"invalid_housing_canonical_history_matched_previous"),
        matchedCurrentTransactionCount:nonnegativeInteger(bar.matchedCurrentTransactionCount,"invalid_housing_canonical_history_matched_current"),
        originCellCount:nonnegativeInteger(bar.originCellCount,"invalid_housing_canonical_history_origin_cells"),
        originPreviousTransactionCoveragePct:nonnegative(bar.originPreviousTransactionCoveragePct,"invalid_housing_canonical_history_origin_coverage"),
        originCellSurvivalPct:nonnegative(bar.originCellSurvivalPct,"invalid_housing_canonical_history_origin_survival"),
        segmentId:bar.primarySegmentId==null?null:String(bar.primarySegmentId),
        status:status as HousingCanonicalDailyRow["status"],
        anchor:status==="anchor",
        strictFixedLogPriceChangePct:nullableFinite(bar.strictFixedLogPriceChangePct,"invalid_housing_canonical_history_strict_return"),
        strictFixedIndexOpen:nullableFinite(bar.strictFixedIndexOpen,"invalid_housing_canonical_history_strict_open"),
        strictFixedIndexHigh:nullableFinite(bar.strictFixedIndexHigh,"invalid_housing_canonical_history_strict_high"),
        strictFixedIndexLow:nullableFinite(bar.strictFixedIndexLow,"invalid_housing_canonical_history_strict_low"),
        strictFixedIndexClose:nullableFinite(bar.strictFixedIndexClose,"invalid_housing_canonical_history_strict_close"),
        strictFixedStatus:strictStatus as HousingCanonicalDailyRow["strictFixedStatus"],
        strictFixedSegmentId:bar.strictFixedSegmentId==null?null:String(bar.strictFixedSegmentId),
      };
      if(Object.prototype.hasOwnProperty.call(bar,"provisional"))row.provisional=Boolean(bar.provisional);
      if((row.status==="anchor"||row.status==="ready")&&!validIndexRow(row))throw new Error("invalid_housing_canonical_history_ready_row");
      if((row.strictFixedStatus==="anchor"||row.strictFixedStatus==="ready")&&!validStrictIndexRow(row))throw new Error("invalid_housing_canonical_history_strict_ready_row");
      out.push(row);
    }
  }
  return mergeCanonicalDailyRows([],out);
}

export function mergeCanonicalDailyRows(current: HousingCanonicalDailyRow[],incoming: HousingCanonicalDailyRow[]){
  const merged=new Map<string,HousingCanonicalDailyRow>();
  for(const row of [...current,...incoming])merged.set(row.endDate,row);
  return [...merged.values()].sort((a,b)=>a.endDate.localeCompare(b.endDate));
}

export function aggregateCanonicalDailyRows(rows: HousingCanonicalDailyRow[],resolution: HousingCanonicalDisplayResolution): HousingCanonicalDisplayRow[] {
  const ordered=mergeCanonicalDailyRows([],rows);
  if(resolution==="1d")return ordered.map(row=>({...row,periodEndDate:row.periodEndDate??row.endDate,resolution,dailyBarCount:1,marketValidDayCount:validIndexRow(row)?1:0}));
  const grouped=new Map<string,{start:string;end:string;members:HousingCanonicalDailyRow[]}>();
  for(const row of ordered){
    const bounds=bucketBounds(row.endDate,resolution),key=`${bounds.start}:${bounds.end}`;
    const bucket=grouped.get(key)??{...bounds,members:[]}; bucket.members.push(row); grouped.set(key,bucket);
  }
  return [...grouped.values()].sort((a,b)=>a.start.localeCompare(b.start)).map(bucket=>{
    const members=[...bucket.members].sort((a,b)=>a.endDate.localeCompare(b.endDate));
    const marketMembers=members.filter(validIndexRow),marketSegments=new Set(marketMembers.map(row=>row.segmentId as string));
    const marketReady=marketMembers.length>0&&marketSegments.size===1,firstMarket=marketReady?marketMembers[0]:null,lastMarket=marketReady?marketMembers.at(-1)!:null;
    const strictMembers=members.filter(validStrictIndexRow),strictSegments=new Set(strictMembers.map(row=>row.strictFixedSegmentId as string));
    const strictReady=strictMembers.length===members.length&&strictMembers.length>0&&strictSegments.size===1,firstStrict=strictReady?strictMembers[0]:null,lastStrict=strictReady?strictMembers.at(-1)!:null;
    const lastMember=members.at(-1)!,hasExplicitProvisional=members.some(row=>Object.prototype.hasOwnProperty.call(row,"provisional"));
    const marketOpen=firstMarket?.open??null,marketClose=lastMarket?.close??null;
    const strictOpen=firstStrict?.strictFixedIndexOpen??null,strictClose=lastStrict?.strictFixedIndexClose??null;
    return {
      startDate:bucket.start,
      endDate:(lastMarket??lastMember).endDate,
      periodEndDate:bucket.end,
      lastObservedDate:lastMember.lastObservedDate,
      open:marketOpen,
      high:marketReady?Math.max(...marketMembers.map(row=>row.high as number)):null,
      low:marketReady?Math.min(...marketMembers.map(row=>row.low as number)):null,
      close:marketClose,
      transactionCount:members.reduce((sum,row)=>sum+row.transactionCount,0),
      medianPriceProxyKrw:lastMember.medianPriceProxyKrw,
      ...(hasExplicitProvisional?{provisional:members.some(row=>row.provisional===true)}:{}),
      primaryLogPriceChangePct:marketReady&&finite(marketOpen)&&marketOpen!==0&&finite(marketClose)?Math.log(marketClose/marketOpen)*100:null,
      matchedCellCount:lastMember.matchedCellCount,
      matchedPreviousTransactionCount:lastMember.matchedPreviousTransactionCount,
      matchedCurrentTransactionCount:lastMember.matchedCurrentTransactionCount,
      originCellCount:lastMember.originCellCount,
      originPreviousTransactionCoveragePct:lastMember.originPreviousTransactionCoveragePct,
      originCellSurvivalPct:lastMember.originCellSurvivalPct,
      segmentId:marketReady?[...marketSegments][0]:null,
      status:marketReady?"ready":"insufficient",
      anchor:marketReady&&marketMembers.some(row=>row.anchor),
      strictFixedLogPriceChangePct:strictReady&&finite(strictOpen)&&strictOpen!==0&&finite(strictClose)?Math.log(strictClose/strictOpen)*100:null,
      strictFixedIndexOpen:strictOpen,
      strictFixedIndexHigh:strictReady?Math.max(...strictMembers.map(row=>row.strictFixedIndexHigh as number)):null,
      strictFixedIndexLow:strictReady?Math.min(...strictMembers.map(row=>row.strictFixedIndexLow as number)):null,
      strictFixedIndexClose:strictClose,
      strictFixedStatus:strictReady?"ready":"insufficient",
      strictFixedSegmentId:strictReady?[...strictSegments][0]:null,
      resolution,
      dailyBarCount:members.length,
      marketValidDayCount:marketMembers.length,
    };
  });
}

export function canonicalDisplayRowsToMarketBars(rows:HousingCanonicalDisplayRow[],analysisDate:Date=new Date()):HousingMarketBar[]{
  const asOfSerial=Math.floor(Date.UTC(analysisDate.getUTCFullYear(),analysisDate.getUTCMonth(),analysisDate.getUTCDate())/DAY_MS);
  const currentMonth=analysisDate.toISOString().slice(0,7);
  return rows.map((row):HousingMarketBar=>{
    const marketReady=(row.status==="anchor"||row.status==="ready")&&finite(row.open)&&finite(row.high)&&finite(row.low)&&finite(row.close)&&Boolean(row.segmentId);
    const strictReady=(row.strictFixedStatus==="anchor"||row.strictFixedStatus==="ready")&&finite(row.strictFixedIndexOpen)&&finite(row.strictFixedIndexHigh)&&finite(row.strictFixedIndexLow)&&finite(row.strictFixedIndexClose)&&Boolean(row.strictFixedSegmentId);
    const sampleStatus=row.transactionCount>=10?"ready":row.transactionCount>=5?"low_sample":"insufficient";
    const periodEnd=row.periodEndDate??row.endDate;
    const reportingAgeDays=Math.max(0,asOfSerial-dateSerial(periodEnd));
    const rowMonth=periodEnd.slice(0,7),hasExplicitProvisional=Object.prototype.hasOwnProperty.call(row,"provisional");
    const provisional=rowMonth===currentMonth&&(row.provisional===true||(!hasExplicitProvisional));
    const initialReportingMature=reportingAgeDays>=30&&!provisional;
    return {
      startDate:row.startDate,
      endDate:row.endDate,
      periodEndDate:periodEnd,
      lastObservedDate:row.lastObservedDate,
      marketIndexValidDayCount:row.marketValidDayCount??(marketReady?1:0),
      marketIndexSegmentId:marketReady?row.segmentId:null,
      transactionCount:row.transactionCount,
      medianPriceProxyKrw:row.medianPriceProxyKrw,
      p10PriceKrw:null,
      p90PriceKrw:null,
      openPriceProxyKrw:null,
      closePriceProxyKrw:null,
      lowPriceProxyKrw:null,
      highPriceProxyKrw:null,
      marketIndexOpen:finite(row.open)?row.open:null,
      marketIndexHigh:finite(row.high)?row.high:null,
      marketIndexLow:finite(row.low)?row.low:null,
      marketIndexClose:finite(row.close)?row.close:null,
      marketIndexStatus:marketReady?"ready":"insufficient",
      marketIndexNormalizedTransactionCount:0,
      marketIndexNormalizedCellDayCount:0,
      sampleStatus,
      provisional,
      reportingAgeDays,
      initialReportingMature,
      revisionRisk:initialReportingMature?"initial_window_elapsed":"initial_window_open",
      fixedCellLogPriceChangePct:row.strictFixedLogPriceChangePct??null,
      fixedCellIndex:strictReady?(row.strictFixedIndexClose as number):null,
      fixedCellIndexOpen:strictReady?(row.strictFixedIndexOpen as number):null,
      fixedCellIndexHigh:strictReady?(row.strictFixedIndexHigh as number):null,
      fixedCellIndexLow:strictReady?(row.strictFixedIndexLow as number):null,
      fixedCellSegmentId:strictReady?(row.strictFixedSegmentId as string):null,
      matchedCellCount:row.matchedCellCount,
      matchedPreviousTransactionCount:row.matchedPreviousTransactionCount,
      matchedCurrentTransactionCount:row.matchedCurrentTransactionCount,
      previousTransactionCoveragePct:row.originPreviousTransactionCoveragePct,
      currentTransactionCoveragePct:row.originCellSurvivalPct,
      originPreviousTransactionCoveragePct:row.originPreviousTransactionCoveragePct,
      originCellCount:row.originCellCount,
      originCellSurvivalPct:row.originCellSurvivalPct,
      fixedCellStatus:strictReady?"ready":"insufficient",
      observedMarketLogPriceChangePct:row.primaryLogPriceChangePct,
      calculationLookaheadFree:true,
      methodAsReleasedPitSafe:false,
    };
  });
}

export function buildCanonicalHousingTechnicalPoints(
  rows: HousingCanonicalDisplayRow[],
  resolution: HousingCanonicalDisplayResolution,
  preset: HousingMacdPreset = "standard",
) {
  return buildHousingTechnicalPoints(canonicalDisplayRowsToMarketBars(rows), resolution, "median_proxy", preset);
}

export interface HousingCanonicalHistoryCoverage {
  segments:Array<{startMonth:string;endMonth:string;monthCount:number}>;
  availableCompleteMonthCount:number;
}
export function canonicalHistoryCoverage(reference:unknown,scope:HousingDetailedHistoryScope):HousingCanonicalHistoryCoverage{
  const document=record(reference,"invalid_housing_canonical_history_reference"),scopes=record(document.scopes,"invalid_housing_canonical_history_reference_scopes"),entry=record(scopes[scope],"invalid_housing_canonical_history_scope"),index=record(entry.index,"invalid_housing_canonical_history_index"),resolutions=record(index.resolutions,"invalid_housing_canonical_history_resolutions"),daily=record(resolutions["1d"],"invalid_housing_canonical_history_resolution"),raw=daily.completeMonthSegments;
  if(!Array.isArray(raw))throw new Error("invalid_housing_canonical_history_segments");
  const segments=raw.map(value=>{const x=record(value,"invalid_housing_canonical_history_segment"),startMonth=String(x.startMonth??""),endMonth=String(x.endMonth??""),monthCount=Number(x.monthCount);if(!/^\d{4}-\d{2}$/.test(startMonth)||!/^\d{4}-\d{2}$/.test(endMonth)||!Number.isInteger(monthCount)||monthCount<1)throw new Error("invalid_housing_canonical_history_segment");return{startMonth,endMonth,monthCount};});
  return{segments,availableCompleteMonthCount:segments.reduce((sum,item)=>sum+item.monthCount,0)};
}
export function buildCanonicalHousingChartScopeData(input:{sourceGenerationId:string;scope:HousingDetailedHistoryScope;bandId:string;rows:HousingCanonicalDailyRow[];coverage:HousingCanonicalHistoryCoverage}):HousingMarketChartScopeData{
  const resolutions=[...HOUSING_CANONICAL_DISPLAY_RESOLUTIONS];
  const emptyBands=()=>Object.fromEntries(HOUSING_TAPE_CONTRACT.bands.map(band=>[band.id,[] as HousingMarketBar[]]));
  const bars=Object.fromEntries(resolutions.map(resolution=>{const byBand=emptyBands();byBand[input.bandId]=canonicalDisplayRowsToMarketBars(aggregateCanonicalDailyRows(input.rows,resolution));return[resolution,byBand];})) as HousingMarketChartScopeData["bars"];
  const months=[...new Set(input.rows.map(row=>row.endDate.slice(0,7)))].sort(),latest=input.rows.at(-1)?.endDate??"1970-01-01";
  const latestMonth=months.at(-1)??null,currentMonth=new Date().toISOString().slice(0,7),hasExplicitProvisional=input.rows.some(row=>Object.prototype.hasOwnProperty.call(row,"provisional"));
  const currentMonthProvisional=input.rows.some(row=>row.provisional===true&&row.endDate.slice(0,7)===currentMonth)||(!hasExplicitProvisional&&latestMonth===currentMonth);
  return{
    analysisGenerationId:input.sourceGenerationId,
    schemaVersion:"canonical-1d",
    generatedAt:`${latest}T00:00:00.000Z`,
    derivedFromRtmsGeneratedAt:null,
    scope:input.scope,
    scopeLabel:HOUSING_HTS_SCOPE_BY_ID[input.scope].label,
    dataKind:"rtms_derived",
    coverage:{startMonth:months[0]??null,endMonth:months.at(-1)??null,completeMonths:months,availableMonthSegments:input.coverage.segments,availableCompleteMonthCount:input.coverage.availableCompleteMonthCount,tradeCount:input.rows.reduce((sum,row)=>sum+row.transactionCount,0),currentMonthProvisional},
    bands:HOUSING_TAPE_CONTRACT.bands.map(band=>({id:band.id,label:band.label})),
    resolutions,
    fixedCellGuard:{cellDefinition:"Strict Fixed diagnostic · canonical 1D origin-cohort fixed-cell",minimumMatchedCells:5,minimumMatchedTransactionsEachBar:10,minimumTransactionCoveragePctEachBar:30,minimumOriginPreviousTransactionCoveragePct:30,minimumOriginCellSurvivalPct:30,note:"기존 Quality-adjusted canonical 1D가 HTS primary이며 Strict Fixed는 진단선입니다. 3D/7D/14D/1M은 같은 1D에서만 OHLC 집계합니다."},
    bars,
    ticksByBand:Object.fromEntries(HOUSING_TAPE_CONTRACT.bands.map(band=>[band.id,[]]))
  };
}

export async function loadCanonicalHousingHistoryPage(client: HousingCanonicalHistoryClient,input:{sourceGenerationId:string;scope:HousingDetailedHistoryScope;bandId?:string;offset?:number},signal?:AbortSignal,enabled=HOUSING_CANONICAL_1D_BROWSER_UI_ENABLED):Promise<HousingCanonicalHistoryPage>{
  if(!enabled)return{status:"disabled",rows:[],nextOffset:input.offset??0,done:true,totalMatchingChunks:0,prefetchedKeys:[]};
  const offset=input.offset??0;
  const result=await client.loadWindow({sourceGenerationId:input.sourceGenerationId,scope:input.scope,resolution:"1d",offset,limit:HOUSING_CANONICAL_1D_PAGE_SIZE,direction:"backward"},signal);
  return{status:"ready",rows:parseDailyRows(result,input.bandId??"all"),nextOffset:result.nextOffset,done:result.done,totalMatchingChunks:result.totalMatchingChunks,prefetchedKeys:[...result.prefetchedKeys]};
}

function clamp(value:number,lo:number,hi:number){return Math.min(hi,Math.max(lo,value));}
export function normalizeCanonicalViewport(view:HousingCanonicalViewport,length:number):HousingCanonicalViewport{
  if(length<=0)return{start:0,end:-1};
  const end=clamp(Math.round(view.end),0,length-1),start=clamp(Math.round(view.start),0,end);
  return{start,end};
}
function presetBars(preset:HousingViewportPreset,resolution:HousingCanonicalDisplayResolution,length:number){
  if(preset==="MAX")return length;
  if(resolution==="1m"){
    const months=preset==="1M"?2:preset==="3M"?4:preset==="6M"?7:13;
    return Math.min(length,months);
  }
  const days=preset==="1M"?31:preset==="3M"?92:preset==="6M"?183:366;
  return Math.min(length,Math.max(MIN_VISIBLE_BARS,Math.ceil(days/resolutionDays(resolution))));
}
export function presetCanonicalViewport(length:number,preset:HousingViewportPreset,resolution:HousingCanonicalDisplayResolution):HousingCanonicalViewport{
  if(length<=0)return{start:0,end:-1};
  const size=presetBars(preset,resolution,length);
  return{start:Math.max(0,length-size),end:length-1};
}
export function panCanonicalViewport(view:HousingCanonicalViewport,length:number,delta:number):HousingCanonicalViewport{
  if(length<=0)return view;
  const current=normalizeCanonicalViewport(view,length),size=current.end-current.start+1;
  if(size>=length)return current;
  const start=clamp(current.start+Math.round(delta),0,length-size);
  return{start,end:start+size-1};
}
export function zoomCanonicalViewport(view:HousingCanonicalViewport,length:number,factor:number,anchor=.5,resolution:HousingCanonicalDisplayResolution="1d"):HousingCanonicalViewport{
  if(length<=0)return view;
  const current=normalizeCanonicalViewport(view,length),oldSize=current.end-current.start+1;
  const minimum=resolution==="1m"?2:MIN_VISIBLE_BARS;
  const nextSize=clamp(Math.round(oldSize*factor),Math.min(minimum,length),length);
  const a=clamp(anchor,0,1),pivot=current.start+a*Math.max(0,oldSize-1);
  let start=Math.round(pivot-a*Math.max(0,nextSize-1)); start=clamp(start,0,Math.max(0,length-nextSize));
  return{start,end:start+nextSize-1};
}
export function viewportForCanonicalDateRange<T extends {startDate:string;endDate:string}>(rows:T[],startDate:string,endDate:string,fallback:HousingCanonicalViewport):HousingCanonicalViewport{
  if(!rows.length)return{start:0,end:-1};
  const first=rows.findIndex(row=>row.endDate>=startDate);
  let last=-1; for(let i=rows.length-1;i>=0;i--){if(rows[i].startDate<=endDate){last=i;break;}}
  if(first<0||last<first)return normalizeCanonicalViewport(fallback,rows.length);
  return{start:first,end:last};
}
